/**
 * Admin Gold Diggers Management Dashboard
 * 
 * Teams overview, pool history, payout audit, winner reconciliation.
 * v4.3.16 — 2026-04-14
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getServiceRoleClient } from '../../lib/serviceClient';
import { getSessionHeaders } from '../../lib/serverSession';
import { payoutDistributionForDate } from '../../lib/goldDiggersConstants.js';

type AdminTab = 'teams' | 'pool-history' | 'payout-audit';

interface TeamRow {
  id: number;
  name: string;
  category: string;
  is_active: boolean;
  created_at: string;
  leader_id: number;
  leader_username?: string;
  member_count?: number;
}

interface PoolDaySummary {
  day: string;
  total: number;
  entry_count: number;
}

interface WinnerRow {
  id: number;
  competition_date: string;
  team_id: number;
  team_name?: string;
  rank: number;
  weighted_score: number;
  amount_awarded: number;
  pool_total: number;
  paid_at: string | null;
}

interface AdminGoldDiggersProps {
  currentUser?: any;
}

export const AdminGoldDiggers: React.FC<AdminGoldDiggersProps> = () => {
  const [activeTab, setActiveTab] = useState<AdminTab>('teams');
  const [teams, setTeams] = useState<TeamRow[]>([]);
  const [poolHistory, setPoolHistory] = useState<PoolDaySummary[]>([]);
  const [winners, setWinners] = useState<WinnerRow[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  // NOTE: In browser, getServiceRoleClient() returns the anon client with user JWT.
  // Admin-only RLS policies on performance_pool_ledger and user_rank_history check
  // auth.email() against admin_users, so non-admin users get zero rows.
  // Public tables (teams, performance_pool_winners) have authenticated SELECT for all.
  const supabase = getServiceRoleClient();

  // ─── Load Teams ─────────────────────────────────────────────────────
  const loadTeams = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('teams')
      .select('id, name, category, is_active, created_at, leader_id, users!leader_id(username)')
      .order('created_at', { ascending: false })
      .limit(200);

    if (data) {
      // Get member counts via SQL GROUP BY — no row fetching
      const teamIds = data.map((t: any) => t.id);
      const { data: memberCounts } = await supabase.rpc('get_team_member_counts', {
        p_team_ids: teamIds
      });

      const countMap: Record<number, number> = {};
      (memberCounts || []).forEach((m: any) => {
        countMap[m.team_id] = parseInt(m.member_count) || 0;
      });

      setTeams(data.map((t: any) => ({
        ...t,
        leader_username: t.users?.username || 'Unknown',
        member_count: countMap[t.id] || 0
      })));
    }
    setLoading(false);
  }, [supabase]);

  // ─── Load Pool History ──────────────────────────────────────────────
  // SQL GROUP BY via RPC — no row fetching, scales to millions of ledger entries
  // v4.8.253: routed through the admin server endpoint. get_pool_history_by_day is
  // revoked from anon/authenticated, so calling it straight from the browser returned
  // 403 and this chart silently stayed empty. The RPC stays service_role-only.
  const loadPoolHistory = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch('/api/admin/pool-history', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ limit: 90 })
      });
      const json = await resp.json();
      const data = json?.data;
      if (Array.isArray(data)) {
        setPoolHistory(data.map((d: any) => ({
          day: d.day,
          total: parseFloat(d.total) || 0,
          entry_count: parseInt(d.entry_count) || 0
        })));
      }
    } catch (e) {
      console.error('Failed to load pool history:', e);
    }
    setLoading(false);
  }, []);

  // ─── Load Winners ───────────────────────────────────────────────────
  const loadWinners = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('performance_pool_winners')
      .select('id, competition_date, team_id, rank, weighted_score, amount_awarded, pool_total, paid_at, teams!team_id(name)')
      .order('competition_date', { ascending: false })
      .order('rank', { ascending: true })
      .limit(500);

    if (data) {
      setWinners(data.map((w: any) => ({
        ...w,
        team_name: w.teams?.name || `Team #${w.team_id}`
      })));
    }
    setLoading(false);
  }, [supabase]);

  useEffect(() => {
    if (activeTab === 'teams') loadTeams();
    else if (activeTab === 'pool-history') loadPoolHistory();
    else if (activeTab === 'payout-audit') loadWinners();
  }, [activeTab, loadTeams, loadPoolHistory, loadWinners]);

  const filteredTeams = teams.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (t.leader_username || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Payout anomaly detection
  const getPayoutAnomaly = (w: WinnerRow): string | null => {
    if (!w.paid_at) return 'UNPAID — paid_at is NULL';
    const expectedPct = payoutDistributionForDate(w.competition_date)[w.rank - 1];
    if (expectedPct === undefined) return 'INVALID RANK';
    const expectedAmount = parseFloat((w.pool_total * expectedPct).toFixed(2));
    if (Math.abs(w.amount_awarded - expectedAmount) > 0.02) {
      return `MISMATCH — expected $${expectedAmount.toFixed(2)} (${(expectedPct * 100).toFixed(0)}%), got $${w.amount_awarded.toFixed(2)}`;
    }
    return null;
  };

  const tabStyle = (id: AdminTab) => ({
    padding: '10px 20px', cursor: 'pointer', fontWeight: '600' as const, fontSize: '14px',
    borderRadius: '8px 8px 0 0', transition: 'all 0.2s',
    backgroundColor: activeTab === id ? '#1e1e1e' : 'transparent',
    color: activeTab === id ? '#FFD700' : '#888',
    border: activeTab === id ? '1px solid #333' : '1px solid transparent',
    borderBottom: activeTab === id ? '1px solid #1e1e1e' : '1px solid #333'
  });

  return (
    <div>
      <h3 style={{ fontSize: '20px', color: '#F59E0B', marginBottom: '16px', fontWeight: '600' }}>
        ⛏️ Gold Diggers Management
      </h3>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', borderBottom: '1px solid #333', marginBottom: '20px' }}>
        <button onClick={() => setActiveTab('teams')} style={tabStyle('teams')}>👥 Teams Overview</button>
        <button onClick={() => setActiveTab('pool-history')} style={tabStyle('pool-history')}>💰 Pool History</button>
        <button onClick={() => setActiveTab('payout-audit')} style={tabStyle('payout-audit')}>🔍 Payout Audit</button>
      </div>

      {loading && <p style={{ color: '#888', textAlign: 'center', padding: '40px' }}>Loading...</p>}

      {/* ── TEAMS OVERVIEW ─────────────────────────────────── */}
      {!loading && activeTab === 'teams' && (
        <div>
          <div style={{ marginBottom: '16px' }}>
            <input
              type="text" placeholder="Search teams or leaders..."
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              style={{ width: '100%', maxWidth: '400px', padding: '10px 16px', borderRadius: '8px', border: '1px solid #333', backgroundColor: '#1a1a1a', color: '#fff', fontSize: '14px' }}
            />
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #333', color: '#888', textTransform: 'uppercase', fontSize: '11px' }}>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Team Name</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Leader</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Category</th>
                  <th style={{ padding: '10px', textAlign: 'center' }}>Members</th>
                  <th style={{ padding: '10px', textAlign: 'center' }}>Active</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Created</th>
                </tr>
              </thead>
              <tbody>
                {filteredTeams.map(t => (
                  <tr key={t.id} style={{ borderBottom: '1px solid #222', color: '#ccc' }}>
                    <td style={{ padding: '10px', fontWeight: '600', color: '#FFD700' }}>{t.name}</td>
                    <td style={{ padding: '10px' }}>{t.leader_username}</td>
                    <td style={{ padding: '10px' }}><span style={{ padding: '2px 8px', borderRadius: '12px', backgroundColor: '#333', fontSize: '11px', textTransform: 'uppercase' }}>{t.category}</span></td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{t.member_count}</td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{t.is_active ? '✅' : '❌'}</td>
                    <td style={{ padding: '10px', color: '#888' }}>{new Date(t.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredTeams.length === 0 && <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>No teams found.</p>}
          </div>
          <p style={{ color: '#555', fontSize: '12px', marginTop: '12px' }}>Total: {filteredTeams.length} teams</p>
        </div>
      )}

      {/* ── POOL HISTORY ──────────────────────────────────── */}
      {!loading && activeTab === 'pool-history' && (
        <div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #333', color: '#888', textTransform: 'uppercase', fontSize: '11px' }}>
                <th style={{ padding: '10px', textAlign: 'left' }}>Date</th>
                <th style={{ padding: '10px', textAlign: 'right' }}>Pool Total</th>
                <th style={{ padding: '10px', textAlign: 'right' }}>Entries</th>
              </tr>
            </thead>
            <tbody>
              {poolHistory.map(d => (
                <tr key={d.day} style={{ borderBottom: '1px solid #222', color: '#ccc' }}>
                  <td style={{ padding: '10px' }}>{d.day}</td>
                  <td style={{ padding: '10px', textAlign: 'right', color: '#4ade80', fontWeight: '600', fontFamily: 'monospace' }}>${d.total.toFixed(2)}</td>
                  <td style={{ padding: '10px', textAlign: 'right' }}>{d.entry_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {poolHistory.length === 0 && <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>No pool entries yet.</p>}
        </div>
      )}

      {/* ── PAYOUT AUDIT ──────────────────────────────────── */}
      {!loading && activeTab === 'payout-audit' && (
        <div>
          <p style={{ color: '#888', fontSize: '12px', marginBottom: '12px' }}>
            Rows highlighted <span style={{ color: '#f87171' }}>red</span> have anomalies (unpaid or amount mismatch vs expected distribution).
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #333', color: '#888', textTransform: 'uppercase', fontSize: '11px' }}>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Date</th>
                  <th style={{ padding: '10px', textAlign: 'center' }}>Rank</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Team</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Pool</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Awarded</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Expected</th>
                  <th style={{ padding: '10px', textAlign: 'center' }}>Paid</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Flag</th>
                </tr>
              </thead>
              <tbody>
                {winners.map(w => {
                  const anomaly = getPayoutAnomaly(w);
                  const expectedPct = payoutDistributionForDate(w.competition_date)[w.rank - 1] || 0;
                  const expectedAmt = parseFloat((w.pool_total * expectedPct).toFixed(2));
                  return (
                    <tr key={w.id} style={{ borderBottom: '1px solid #222', color: '#ccc', backgroundColor: anomaly ? 'rgba(239,68,68,0.08)' : 'transparent' }}>
                      <td style={{ padding: '10px' }}>{w.competition_date}</td>
                      <td style={{ padding: '10px', textAlign: 'center', fontWeight: '700', color: w.rank <= 3 ? '#FFD700' : '#fff' }}>#{w.rank}</td>
                      <td style={{ padding: '10px' }}>{w.team_name}</td>
                      <td style={{ padding: '10px', textAlign: 'right', fontFamily: 'monospace' }}>${w.pool_total.toFixed(2)}</td>
                      <td style={{ padding: '10px', textAlign: 'right', fontFamily: 'monospace', color: anomaly ? '#f87171' : '#4ade80' }}>${w.amount_awarded.toFixed(2)}</td>
                      <td style={{ padding: '10px', textAlign: 'right', fontFamily: 'monospace', color: '#888' }}>${expectedAmt.toFixed(2)}</td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>{w.paid_at ? '✅' : '❌'}</td>
                      <td style={{ padding: '10px', color: '#f87171', fontSize: '11px' }}>{anomaly || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {winners.length === 0 && <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>No payouts recorded yet.</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGoldDiggers;
